import {
  AlertTriangle,
  ArrowDown01,
  ArrowDownAZ,
  ArrowLeft,
  ArrowRightLeft,
  ArrowUp01,
  ArrowUpAZ,
  BarChart3,
  Bell,
  BookOpen,
  BookOpenCheck,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Cloud,
  Eye,
  EyeOff,
  FileDown,
  Filter,
  GraduationCap,
  GripVertical,
  LayoutDashboard,
  LoaderCircle,
  LogOut,
  Menu,
  Pencil,
  Plus,
  Search,
  Settings,
  Settings2,
  TrendingUp,
  Trash2,
  Undo2,
  Redo2,
  Upload,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { ClipboardEvent, KeyboardEvent, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { api, mutationActivity } from "./api";
import { navigate } from "./App";
import ClassModal from "./components/ClassModal";
import ScoreColumnModal from "./components/ScoreColumnModal";
import ImportModal from "./components/ImportModal";
import Logo from "./components/Logo";
import StudentImportModal from "./components/StudentImportModal";
import StudentStatusSelect from "./components/StudentStatusSelect";
import StudentTransferModal from "./components/StudentTransferModal";
import { scoreColumnClipboard, scoreRangeStudents, scoreRangeValues } from "./scoreCopy";
import type { ScoreRange } from "./scoreCopy";
import type { AcademicYear, ClassCategory, ClassSummary, Gradebook, ImportResult, ScoreColumn, Student, StudentImportResult } from "./types";

type SortState = { key: string; direction: "asc" | "desc" };
type Toast = { type: "success" | "error"; message: string };
type DashboardPage = "tong-quan" | "diem" | "hoc-sinh" | "bao-cao" | "cai-dat" | "huong-dan";
const pageLabels: Record<DashboardPage, string> = {
  "tong-quan": "Tổng quan", diem: "Quản lý điểm", "hoc-sinh": "Học sinh",
  "bao-cao": "Báo cáo tiến bộ", "cai-dat": "Cài đặt", "huong-dan": "Hướng dẫn",
};

function averageOf(student: Student, columns: ScoreColumn[]) {
  let total = 0;
  let weights = 0;
  for (const column of columns) {
    const value = student.scores[column.id];
    if (typeof value === "number") {
      total += value * column.weight;
      weights += column.weight;
    }
  }
  return weights ? Math.round((total / weights) * 10) / 10 : null;
}

function scoreTone(score: number | null) {
  if (score === null) return "empty";
  if (score < 5) return "low";
  if (score >= 8) return "high";
  return "medium";
}

function matchesScoreFilter(value: number | null, input: string) {
  const query = input.trim().replace(",", ".");
  if (!query) return true;
  if (query === "trống" || query === "-") return value === null;
  if (value === null) return false;
  const range = query.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);
  if (range) return value >= Number(range[1]) && value <= Number(range[2]);
  const comparison = query.match(/^(>=|<=|>|<|=)?\s*(\d+(?:\.\d+)?)$/);
  if (!comparison) return String(value).includes(query);
  const target = Number(comparison[2]);
  switch (comparison[1]) {
    case ">=": return value >= target;
    case "<=": return value <= target;
    case ">": return value > target;
    case "<": return value < target;
    default: return value === target;
  }
}

type ScoreCellProps = {
  value: number | null;
  studentId: string;
  columnId: string;
  component?: "multipleChoice" | "written";
  onSaved: (result: { value: number | null; multipleChoice: number | null; written: number | null }) => void;
  onError: (message: string) => void;
  onPasteColumn: (text: string) => void;
  selected: boolean;
};

function ScoreCell({ value, studentId, columnId, component, onSaved, onError, onPasteColumn, selected }: ScoreCellProps) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(value === null ? "" : String(value)), [value]);

  const save = async () => {
    const normalized = draft.trim().replace(",", ".");
    const nextValue = normalized === "" ? null : Number(normalized);
    if (nextValue !== null && (!Number.isFinite(nextValue) || nextValue < 0 || nextValue > 10)) {
      onError("Điểm phải nằm trong khoảng từ 0 đến 10.");
      setDraft(value === null ? "" : String(value));
      return;
    }
    const rounded = nextValue === null ? null : Math.round(nextValue * 100) / 100;
    if (rounded === value) return;
    setSaving(true);
    try {
      const result = await api.updateScore(studentId, columnId, rounded, component);
      onSaved(result);
    } catch (error) {
      setDraft(value === null ? "" : String(value));
      onError(error instanceof Error ? error.message : "Không thể lưu điểm.");
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") event.currentTarget.blur();
    if (event.key === "Escape") {
      setDraft(value === null ? "" : String(value));
      event.currentTarget.blur();
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text/plain");
    if (!/[\r\n\t]/.test(text)) return;
    event.preventDefault();
    onPasteColumn(text);
  };

  return (
    <div data-score-student={studentId} data-score-column={columnId} data-score-component={component || "total"} className={`score-cell score-cell--${scoreTone(value)} ${saving ? "is-saving" : ""} ${selected ? "is-selected" : ""}`}>
      <input data-history-ready={!saving && (draft.trim().replace(",", ".") === (value === null ? "" : String(value))) ? "true" : undefined} aria-label={component === "multipleChoice" ? "Điểm trắc nghiệm" : component === "written" ? "Điểm tự luận" : "Nhập điểm"} inputMode="decimal" value={draft} onChange={(event) => setDraft(event.target.value)} onBlur={() => void save()} onKeyDown={onKeyDown} onPaste={onPaste} />
      {saving && <LoaderCircle className="spin" size={13} />}
    </div>
  );
}

export default function Dashboard({ page: requestedPage = "diem" }: { page?: string }) {
  const page: DashboardPage = requestedPage in pageLabels ? requestedPage as DashboardPage : "diem";
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [classes, setClasses] = useState<ClassSummary[]>([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [gradebook, setGradebook] = useState<Gradebook | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookLoading, setBookLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<Student["status"] | "all">("all");
  const [transferStudent, setTransferStudent] = useState<Student | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortState>({ key: "studentCode", direction: "asc" });
  const [scoreSelection, setScoreSelection] = useState<(ScoreRange & { context: string }) | null>(null);
  const scoreTable = useRef<HTMLTableElement>(null);
  const [showStatusColumn, setShowStatusColumn] = useState(() => window.localStorage.getItem("gradebook-show-status") === "true");
  const [showActionColumn, setShowActionColumn] = useState(() => window.localStorage.getItem("gradebook-show-actions") === "true");
  const [importColumnId, setImportColumnId] = useState<string | null>(null);
  const [importStudentOrder, setImportStudentOrder] = useState<Student[]>([]);
  const [pasteTarget, setPasteTarget] = useState<{ text: string; studentCode: string; component?: "multipleChoice" | "written" } | null>(null);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [editingColumn, setEditingColumn] = useState<ScoreColumn | null>(null);
  const [showStudentImport, setShowStudentImport] = useState(false);
  const [classModal, setClassModal] = useState<"create" | "edit" | null>(null);
  const [classModalDefaultCategory, setClassModalDefaultCategory] = useState<ClassCategory>("regular");
  const [classCategoryFilter, setClassCategoryFilter] = useState<"all" | ClassCategory>(() => (window.localStorage.getItem("dashboard-class-category-filter") as "all" | ClassCategory) || "all");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.localStorage.getItem("dashboard-sidebar-collapsed") === "true");
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [actionHistory, setActionHistory] = useState<Awaited<ReturnType<typeof api.history>>>({ undo: null, redo: null, undoToken: null, redoToken: null, blocked: false });
  const [historyBusy, setHistoryBusy] = useState(false);
  const historyRunning = useRef(false);
  const activity = useSyncExternalStore(mutationActivity.subscribe, mutationActivity.snapshot);
  const modalOpen = Boolean(importColumnId || showColumnModal || editingColumn || showStudentImport || transferStudent || classModal);

  const regularClasses = useMemo(() => classes.filter((c) => c.category === "regular"), [classes]);
  const extraClasses = useMemo(() => classes.filter((c) => c.category === "extra"), [classes]);
  const visibleClasses = useMemo(() => {
    if (classCategoryFilter === "regular") return regularClasses;
    if (classCategoryFilter === "extra") return extraClasses;
    return classes;
  }, [classCategoryFilter, classes, regularClasses, extraClasses]);

  const handleCategoryFilterChange = (filter: "all" | ClassCategory) => {
    setClassCategoryFilter(filter);
    try {
      window.localStorage.setItem("dashboard-class-category-filter", filter);
    } catch {}
    if (filter !== "all") {
      const filtered = classes.filter((c) => c.category === filter);
      if (filtered.length > 0 && !filtered.some((c) => c.id === selectedClassId)) {
        setSelectedClassId(filtered[0].id);
      }
    }
  };


  useEffect(() => {
    document.title = "Quản lý | Thầy Linh dạy Toán";
    const initialize = async () => {
      try {
        const session = await api.session();
        if (!session.authenticated) {
          navigate("/dang-nhap");
          return;
        }
        const result = await api.academicYears();
        setAcademicYears(result.years);
        setSelectedYear(result.currentYear);
      } catch {
        navigate("/dang-nhap");
      } finally {
        setLoading(false);
      }
    };
    void initialize();
  }, []);

  useEffect(() => { document.title = `${pageLabels[page]} | Thầy Linh dạy Toán`; }, [page]);

  useEffect(() => { window.localStorage.setItem("gradebook-show-status", String(showStatusColumn)); }, [showStatusColumn]);
  useEffect(() => { window.localStorage.setItem("gradebook-show-actions", String(showActionColumn)); }, [showActionColumn]);

  useEffect(() => {
    if (!selectedYear) return;
    let cancelled = false;
    setBookLoading(true);
    setGradebook(null);
    setSelectedClassId("");
    api.classes(selectedYear)
      .then((result) => {
        if (cancelled) return;
        setClasses(result.classes);
        if (result.classes[0]) setSelectedClassId(result.classes[0].id);
        else setBookLoading(false);
      })
      .catch((error) => {
        if (!cancelled) {
          setClasses([]);
          setBookLoading(false);
          showToast("error", error instanceof Error ? error.message : "Không thể tải danh sách lớp.");
        }
      });
    return () => { cancelled = true; };
  }, [selectedYear]);

  useEffect(() => {
    if (!selectedClassId) return;
    let cancelled = false;
    setBookLoading(true);
    setGradebook(null);
    setFilters({});
    api.gradebook(selectedClassId)
      .then((result) => { if (!cancelled) setGradebook(result); })
      .catch((error) => showToast("error", error instanceof Error ? error.message : "Không thể tải bảng điểm."))
      .finally(() => { if (!cancelled) setBookLoading(false); });
    return () => { cancelled = true; };
  }, [selectedClassId]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3800);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const showToast = (type: Toast["type"], message: string) => setToast({ type, message });

  useEffect(() => {
    if (loading || activity.pending) return;
    let cancelled = false;
    const refresh = () => {
      if (mutationActivity.snapshot().pending) return;
      api.history().then((result) => { if (!cancelled) setActionHistory(result); })
        .catch((error) => { if (!cancelled) showToast("error", error instanceof Error ? error.message : "Không thể tải lịch sử thao tác."); });
    };
    refresh();
    window.addEventListener("focus", refresh);
    return () => { cancelled = true; window.removeEventListener("focus", refresh); };
  }, [loading, activity]);

  const runHistory = async (direction: "undo" | "redo") => {
    if (historyRunning.current || modalOpen || loading || bookLoading) return;
    const activeInput = document.activeElement;
    if (activeInput instanceof HTMLInputElement && activeInput.closest(".score-cell")) {
      const value = activeInput.value.trim().replace(",", ".");
      if (value && (!Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 10)) {
        showToast("error", "Điểm phải nằm trong khoảng từ 0 đến 10. Hãy sửa điểm trước khi hoàn tác/làm lại.");
        return;
      }
    }
    const failuresBeforeSave = mutationActivity.snapshot().failures;
    historyRunning.current = true;
    // Clicking a history button first saves the score being edited on blur.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setHistoryBusy(true);
    let replayed = false;
    try {
      await mutationActivity.settled();
      if (mutationActivity.snapshot().failures !== failuresBeforeSave) return;
      const current = await api.history();
      const token = direction === "undo" ? current.undoToken : current.redoToken;
      if (!token) {
        setActionHistory(current);
        showToast("error", current.blocked ? "Dữ liệu đã thay đổi từ phiên khác; không thể dùng lịch sử cũ." : "Không có thao tác để hoàn tác/làm lại.");
        return;
      }
      const result = await api[direction](token);
      replayed = true;
      setActionHistory(result);
      const updated = await api.classes(selectedYear);
      const classId = updated.classes.some((item) => item.id === selectedClassId) ? selectedClassId : updated.classes[0]?.id || "";
      const updatedBook = classId ? await api.gradebook(classId) : null;
      setClasses(updated.classes);
      setSelectedClassId(classId);
      setGradebook(updatedBook);
      setFilters({});
      showToast("success", `${direction === "undo" ? "Đã hoàn tác" : "Đã làm lại"}: ${result.label}.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Không thể hoàn tác/làm lại.";
      showToast("error", replayed ? `Thao tác đã lưu nhưng không tải được bảng điểm. Hãy tải lại trang. ${message}` : message);
      api.history().then(setActionHistory).catch(() => {});
    } finally {
      historyRunning.current = false;
      setHistoryBusy(false);
    }
  };

  useEffect(() => {
    const shortcut = (event: globalThis.KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || event.repeat || event.isComposing || event.defaultPrevented) return;
      const key = event.key.toLowerCase();
      if (key !== "z" && key !== "y") return;
      const target = event.target;
      if (target instanceof HTMLElement && target.closest("input, textarea, select, [contenteditable='true']") && target.dataset.historyReady !== "true") return;
      if (modalOpen || loading || bookLoading || historyBusy || activity.pending) return;
      event.preventDefault();
      const direction = key === "z" ? "undo" : "redo";
      if (actionHistory[direction]) void runHistory(direction);
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  });

  const activeStudents = useMemo(() => gradebook?.students.filter((student) => student.status === "active") || [], [gradebook]);

  const metrics = useMemo(() => {
    if (!gradebook) return { average: null, complete: 0, below: 0, totalCells: 0 };
    const averages = activeStudents.map((student) => averageOf(student, gradebook.columns)).filter((value): value is number => value !== null);
    const average = averages.length ? averages.reduce((sum, value) => sum + value, 0) / averages.length : null;
    let filled = 0;
    let below = 0;
    for (const student of activeStudents) {
      for (const column of gradebook.columns) {
        const value = student.scores[column.id];
        if (typeof value === "number") {
          filled += 1;
          if (value < 5) below += 1;
        }
      }
    }
    const totalCells = activeStudents.length * gradebook.columns.length;
    return { average, complete: totalCells ? Math.round((filled / totalCells) * 100) : 0, below, totalCells };
  }, [gradebook, activeStudents]);

  const filteredStudents = useMemo(() => {
    if (!gradebook) return [];
    const global = search.trim().toLocaleLowerCase("vi");
    const rows = gradebook.students.filter((student) => {
      if (statusFilter !== "all" && student.status !== statusFilter) return false;
      const searchable = `${student.studentCode} ${student.lastName} ${student.firstName} ${student.email}`.toLocaleLowerCase("vi");
      if (global && !searchable.includes(global)) return false;
      if (filters.studentCode && !student.studentCode.toLowerCase().includes(filters.studentCode.toLowerCase())) return false;
      if (filters.lastName && !student.lastName.toLocaleLowerCase("vi").includes(filters.lastName.toLocaleLowerCase("vi"))) return false;
      if (filters.firstName && !student.firstName.toLocaleLowerCase("vi").includes(filters.firstName.toLocaleLowerCase("vi"))) return false;
      return gradebook.columns.every((column) => !filters[column.id] || matchesScoreFilter(student.scores[column.id] ?? null, filters[column.id]));
    });

    return [...rows].sort((left, right) => {
      let leftValue: string | number | null;
      let rightValue: string | number | null;
      if (sort.key in left.scores || gradebook.columns.some((column) => column.id === sort.key)) {
        leftValue = left.scores[sort.key] ?? null;
        rightValue = right.scores[sort.key] ?? null;
      } else {
        leftValue = left[sort.key as keyof Pick<Student, "studentCode" | "lastName" | "firstName">];
        rightValue = right[sort.key as keyof Pick<Student, "studentCode" | "lastName" | "firstName">];
      }
      if (leftValue === null) return 1;
      if (rightValue === null) return -1;
      const result = typeof leftValue === "number" && typeof rightValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), "vi", { numeric: true });
      return sort.direction === "asc" ? result : -result;
    });
  }, [gradebook, search, filters, sort, statusFilter]);

  // Keep selection tied to the visible row order and column modes, even while scores save.
  const scoreSelectionContext = JSON.stringify([page, selectedClassId, filteredStudents.map((student) => student.id), gradebook?.columns.map((column) => [column.id, column.mode])]);
  const scoreRange = !modalOpen && !bookLoading && scoreSelection?.context === scoreSelectionContext ? scoreSelection : null;
  const selectedScoreStudents = scoreRangeStudents(filteredStudents, scoreRange);
  const selectedScoreIds = new Set(selectedScoreStudents.map((student) => student.id));
  const isScoreSelected = (studentId: string, columnId: string, component?: ScoreRange["component"]) => Boolean(scoreRange && scoreRange.columnId === columnId && scoreRange.component === component && selectedScoreIds.has(studentId));

  const scoreTarget = (target: EventTarget | null) => target instanceof Element ? target.closest<HTMLElement>("[data-score-student]") : null;
  const selectScore = (cell: HTMLElement, extend: boolean) => {
    const studentId = cell.dataset.scoreStudent!;
    const columnId = cell.dataset.scoreColumn!;
    const component = cell.dataset.scoreComponent === "total" ? undefined : cell.dataset.scoreComponent as ScoreRange["component"];
    const anchorId = extend && scoreRange?.columnId === columnId && scoreRange.component === component ? scoreRange.anchorId : studentId;
    setScoreSelection({ columnId, component, anchorId, endId: studentId, context: scoreSelectionContext });
  };

  useEffect(() => {
    const clearOutsideSelection = (event: PointerEvent) => {
      if (!(event.target instanceof Element) || !scoreTable.current?.contains(event.target) || !scoreTarget(event.target)) setScoreSelection(null);
    };
    document.addEventListener("pointerdown", clearOutsideSelection);
    return () => document.removeEventListener("pointerdown", clearOutsideSelection);
  }, []);

  const onScoreRangeKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    const cell = scoreTarget(event.target);
    if (!cell || event.nativeEvent.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === "Escape") {
      setScoreSelection(null);
      return;
    }
    if (!event.shiftKey || !["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const currentId = scoreRange?.endId ?? cell.dataset.scoreStudent;
    const currentIndex = filteredStudents.findIndex((student) => student.id === currentId);
    const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? filteredStudents.length - 1
      : Math.max(0, Math.min(filteredStudents.length - 1, currentIndex + (event.key === "ArrowDown" ? 1 : -1)));
    const nextStudent = filteredStudents[nextIndex];
    if (!nextStudent) return;
    setScoreSelection({
      columnId: cell.dataset.scoreColumn!,
      component: cell.dataset.scoreComponent === "total" ? undefined : cell.dataset.scoreComponent as ScoreRange["component"],
      anchorId: scoreRange?.anchorId ?? cell.dataset.scoreStudent!,
      endId: nextStudent.id,
      context: scoreSelectionContext,
    });
    const nextCell = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("[data-score-student]")).find((item) => item.dataset.scoreStudent === nextStudent.id && item.dataset.scoreColumn === cell.dataset.scoreColumn && item.dataset.scoreComponent === cell.dataset.scoreComponent);
    nextCell?.scrollIntoView({ block: "nearest", inline: "nearest" });
  };

  const copyScoreRange = (event: ClipboardEvent<HTMLTableElement>) => {
    if (!scoreRange || !scoreTarget(event.target)) return;
    // Let a single input retain normal text-copy behavior; a total has no text editor.
    if (selectedScoreStudents.length < 2 && event.target instanceof HTMLInputElement) return;
    event.preventDefault();
    const values = scoreRangeValues(filteredStudents, scoreRange);
    const inputs = new Map(Array.from(event.currentTarget.querySelectorAll<HTMLElement>("[data-score-student]"))
      .filter((cell) => cell.dataset.scoreColumn === scoreRange.columnId && cell.dataset.scoreComponent === (scoreRange.component || "total"))
      .map((cell) => [cell.dataset.scoreStudent, cell.querySelector("input")]));
    for (let index = 0; index < selectedScoreStudents.length; index += 1) {
      const input = inputs.get(selectedScoreStudents[index].id);
      if (!input) continue;
      const draft = input.value.trim().replace(",", ".");
      const value = draft === "" ? null : Number(draft);
      if (value !== null && (!Number.isFinite(value) || value < 0 || value > 10)) {
        showToast("error", "Hãy sửa điểm không hợp lệ trước khi sao chép.");
        return;
      }
      values[index] = value === null ? null : Math.round(value * 100) / 100;
    }
    event.clipboardData.setData("text/plain", scoreColumnClipboard(values));
    showToast("success", `Đã sao chép ${values.length} ô điểm. Dán vào Excel hoặc Google Sheets bằng Ctrl+V / Cmd+V.`);
  };

  const studentPageRows = useMemo(() => {
    if (!gradebook) return [];
    const query = search.trim().toLocaleLowerCase("vi");
    return gradebook.students.filter((student) => (statusFilter === "all" || student.status === statusFilter) && `${student.studentCode} ${student.lastName} ${student.firstName}`.toLocaleLowerCase("vi").includes(query));
  }, [gradebook, search, statusFilter]);

  const toggleSort = (key: string) => {
    setSort((current) => current.key === key ? { key, direction: current.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" });
  };

  const selectedClass = classes.find((classItem) => classItem.id === selectedClassId);

  const changeStudentStatus = async (student: Student, status: Student["status"]) => {
    try {
      await api.updateStudentStatus(student.id, status);
      setGradebook((current) => current ? { ...current, students: current.students.map((item) => item.id === student.id ? { ...item, status } : item) } : current);
      showToast("success", `Đã chuyển ${student.firstName} sang trạng thái ${status === "active" ? "Đang học" : "Nghỉ"}.`);
    } catch (error) { showToast("error", error instanceof Error ? error.message : "Không thể lưu trạng thái."); }
  };

  const handleStudentTransferred = (targetClassId: string, student: Student) => {
    setTransferStudent(null);
    setClasses((current) => current.map((item) => item.id === selectedClassId ? { ...item, studentCount: Math.max(0, item.studentCount - 1) } : item.id === targetClassId ? { ...item, studentCount: item.studentCount + 1 } : item));
    setSearch("");
    setStatusFilter("all");
    setSelectedClassId(targetClassId);
    showToast("success", `Đã chuyển ${student.lastName} ${student.firstName} đến lớp ${classes.find((item) => item.id === targetClassId)?.name}. Điểm đã được giữ lại.`);
  };

  const openScoreImport = (columnId: string) => {
    setImportStudentOrder([...filteredStudents]);
    setPasteTarget(null);
    setImportColumnId(columnId);
  };

  const openScorePaste = (columnId: string, studentCode: string, text: string, component?: "multipleChoice" | "written") => {
    setImportStudentOrder([...filteredStudents]);
    setPasteTarget({ text, studentCode, component });
    setImportColumnId(columnId);
  };

  const closeScoreImport = () => {
    setImportColumnId(null);
    setPasteTarget(null);
  };

  const changePage = (nextPage: DashboardPage) => {
    navigate(nextPage === "diem" ? "/quan-ly" : `/quan-ly/${nextPage}`);
    setSidebarOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      window.localStorage.setItem("dashboard-sidebar-collapsed", String(!current));
      return !current;
    });
  };

  const reorderColumn = async (targetId: string) => {
    if (!gradebook || !draggedColumnId || draggedColumnId === targetId) return;
    const previous = gradebook.columns;
    const reordered = [...previous];
    const sourceIndex = reordered.findIndex((column) => column.id === draggedColumnId);
    const targetIndex = reordered.findIndex((column) => column.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setGradebook((current) => current ? { ...current, columns: reordered } : current);
    setDraggedColumnId(null);
    try {
      await api.reorderScoreColumns(gradebook.class.id, reordered.map((column) => column.id));
      showToast("success", "Đã lưu thứ tự cột điểm.");
    } catch (error) {
      setGradebook((current) => current ? { ...current, columns: previous } : current);
      showToast("error", error instanceof Error ? error.message : "Không thể đổi thứ tự cột điểm.");
    }
  };

  const handleClassSaved = (classItem: ClassSummary, created: boolean) => {
    setClassModal(null);
    setClasses((current) => created ? [...current, classItem] : current.map((item) => item.id === classItem.id ? classItem : item));
    setSelectedClassId(classItem.id);
    if (classCategoryFilter !== "all" && classCategoryFilter !== classItem.category) {
      setClassCategoryFilter("all");
      try {
        window.localStorage.setItem("dashboard-class-category-filter", "all");
      } catch {}
    }
    const categoryLabel = classItem.category === "extra" ? "lớp dạy thêm" : "lớp chính khóa";
    showToast("success", created ? `Đã tạo ${categoryLabel} ${classItem.name}.` : `Đã lưu thông tin ${categoryLabel} ${classItem.name}.`);
  };

  const handleClassDeleted = (classId: string) => {
    setClassModal(null);
    const remaining = classes.filter((classItem) => classItem.id !== classId);
    setClasses(remaining);
    setSelectedClassId(remaining[0]?.id || "");
    if (!remaining.length) setGradebook(null);
    showToast("success", "Đã xóa lớp và toàn bộ dữ liệu liên quan.");
  };

  const refreshAfterStudentImport = async (result: StudentImportResult) => {
    setShowStudentImport(false);
    if (!selectedClassId) return;
    const [updatedClasses, updatedGradebook] = await Promise.all([api.classes(selectedYear), api.gradebook(selectedClassId)]);
    setClasses(updatedClasses.classes);
    setGradebook(updatedGradebook);
    const notes = [
      `Đã thêm ${result.created}, cập nhật ${result.updated} học sinh.`,
      result.invalid.length ? `${result.invalid.length} dòng lỗi.` : "",
      result.duplicates.length ? `${result.duplicates.length} SBD trùng.` : "",
    ].filter(Boolean).join(" ");
    showToast(result.invalid.length || result.duplicates.length ? "error" : "success", notes);
  };

  const updateLocalScore = (studentId: string, columnId: string, result: { value: number | null; multipleChoice: number | null; written: number | null }) => {
    setGradebook((current) => current ? {
      ...current,
      students: current.students.map((student) => student.id === studentId ? { ...student, scores: { ...student.scores, [columnId]: result.value }, components: { ...student.components, [columnId]: { multipleChoice: result.multipleChoice, written: result.written } } } : student),
    } : current);
  };

  const createScoreColumn = async (name: string, weight: number, mode: ScoreColumn["mode"]) => {
    if (!gradebook) return;
    const { column } = await api.createScoreColumn(gradebook.class.id, name, weight, mode);
    setGradebook((current) => current ? { ...current, columns: [...current.columns, column] } : current);
    setShowColumnModal(false);
    showToast("success", `Đã thêm cột ${column.shortLabel}.`);
  };

  const configureScoreColumn = async (name: string, weight: number, mode: ScoreColumn["mode"]) => {
    if (!gradebook || !editingColumn) return;
    await api.configureScoreColumn(editingColumn.id, name, weight, mode);
    setGradebook(await api.gradebook(gradebook.class.id));
    setEditingColumn(null);
    showToast("success", `Đã cập nhật cột ${name}.`);
  };

  const deleteScoreColumn = async (column: ScoreColumn) => {
    if (!window.confirm(`Xóa cột “${column.shortLabel}” và toàn bộ điểm trong cột này?`)) return;
    try {
      await api.deleteScoreColumn(column.id);
      setGradebook((current) => current ? { ...current, columns: current.columns.filter((item) => item.id !== column.id) } : current);
      setFilters((current) => { const next = { ...current }; delete next[column.id]; return next; });
      showToast("success", `Đã xóa cột ${column.shortLabel}.`);
    } catch (error) { showToast("error", error instanceof Error ? error.message : "Không thể xóa cột điểm."); }
  };

  const renameScoreColumn = async (columnId: string, name: string) => {
    const renamed = await api.renameScoreColumn(columnId, name);
    setGradebook((current) => current && current.columns.some((column) => column.id === columnId) ? {
      ...current,
      columns: current.columns.map((column) => column.id === columnId ? { ...column, label: renamed.label, shortLabel: renamed.shortLabel } : column),
    } : current);
    showToast("success", `Đã đổi tên cột điểm thành ${renamed.shortLabel}.`);
  };

  const refreshAfterImport = async (result: ImportResult) => {
    closeScoreImport();
    if (gradebook) setGradebook(await api.gradebook(gradebook.class.id));
    const notes = [
      `Đã cập nhật ${result.updated} điểm.`,
      result.unmatched.length ? `${result.unmatched.length} SBD không khớp.` : "",
      result.invalid.length ? `${result.invalid.length} điểm không hợp lệ.` : "",
    ].filter(Boolean).join(" ");
    showToast(result.unmatched.length || result.invalid.length ? "error" : "success", notes);
  };

  const exportGradebook = async () => {
    if (!gradebook) return;
    const XLSX = await import("xlsx");
    const data = gradebook.students.map((student) => {
      const row: Record<string, string | number> = {
        SBD: student.studentCode,
        "Họ và tên lót": student.lastName,
        Tên: student.firstName,
        "Trạng thái": student.status === "active" ? "Đang học" : "Nghỉ",
      };
      for (const column of gradebook.columns) {
        if (column.mode === "sum") {
          row[`${column.shortLabel} · Trắc nghiệm`] = student.components[column.id]?.multipleChoice?.toString() ?? "";
          row[`${column.shortLabel} · Tự luận`] = student.components[column.id]?.written?.toString() ?? "";
        }
        row[column.shortLabel] = student.scores[column.id]?.toString() ?? "";
      }
      row["Trung bình"] = averageOf(student, gradebook.columns)?.toString() ?? "";
      return row;
    });
    const sheet = XLSX.utils.json_to_sheet(data);
    sheet["!cols"] = [{ wch: 12 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, ...gradebook.columns.flatMap((column) => Array.from({ length: column.mode === "sum" ? 3 : 1 }, () => ({ wch: 18 }))), { wch: 14 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, gradebook.class.name);
    XLSX.writeFile(workbook, `Bang_diem_${gradebook.class.name}_${gradebook.class.academicYear.replace("–", "-")}.xlsx`);
    showToast("success", "Đã xuất bảng điểm Excel.");
  };

  const logout = async () => {
    try { await api.logout(); } finally { navigate("/dang-nhap"); }
  };

  if (loading) return <div className="app-loading"><Logo /><span className="spinner spinner--dark" /><p>Đang mở sổ điểm…</p></div>;

  return (
    <div className={`dashboard-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <aside inert={historyBusy} className={`dashboard-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="sidebar-brand"><Logo light /><button onClick={() => setSidebarOpen(false)}><X /></button></div>
        <nav>
          <span className="sidebar-label">KHÔNG GIAN LÀM VIỆC</span>
          <button className={page === "tong-quan" ? "active" : ""} title="Tổng quan" onClick={() => changePage("tong-quan")}><LayoutDashboard /><span>Tổng quan</span></button>
          <button className={page === "diem" ? "active" : ""} title="Quản lý điểm" onClick={() => changePage("diem")}><BookOpenCheck /><span>Quản lý điểm</span>{page === "diem" && <ChevronRight />}</button>
          <button className={page === "hoc-sinh" ? "active" : ""} title="Học sinh" onClick={() => changePage("hoc-sinh")}><Users /><span>Học sinh</span></button>
          <button className={page === "bao-cao" ? "active" : ""} title="Báo cáo tiến bộ" onClick={() => changePage("bao-cao")}><BarChart3 /><span>Báo cáo tiến bộ</span></button>
          <span className="sidebar-label">HỆ THỐNG</span>
          <button className={page === "cai-dat" ? "active" : ""} title="Cài đặt" onClick={() => changePage("cai-dat")}><Settings /><span>Cài đặt</span></button>
          <button className={page === "huong-dan" ? "active" : ""} title="Hướng dẫn" onClick={() => changePage("huong-dan")}><CircleHelp /><span>Hướng dẫn</span></button>
        </nav>
        <div className="sidebar-card">
          <span>HỌC KỲ I</span>
          <b>{gradebook?.class.academicYear || selectedYear || "2026–2027"}</b>
          <div><i style={{ width: `${metrics.complete}%` }} /></div>
          <small>{metrics.complete}% dữ liệu đã hoàn tất</small>
        </div>
        <button className="sidebar-logout" title="Đăng xuất" onClick={() => void logout()}><LogOut /><span>Đăng xuất</span></button>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-topbar">
          <div>
            <button className="mobile-menu" aria-label="Mở bảng điều hướng" onClick={() => setSidebarOpen(true)}><Menu /></button>
            <button className="desktop-sidebar-toggle" aria-label={sidebarCollapsed ? "Hiện thanh điều hướng" : "Ẩn thanh điều hướng"} title={sidebarCollapsed ? "Hiện thanh điều hướng" : "Ẩn thanh điều hướng"} onClick={toggleSidebar}><Menu /></button>
            <div className="breadcrumb"><span>{pageLabels[page]}</span><ChevronRight /><b>{gradebook?.class.name || selectedYear || "…"}</b></div>
          </div>
          <div className="topbar-actions">
            <div className="history-actions" role="group" aria-label="Lịch sử thao tác" aria-busy={historyBusy}>
              <button type="button" aria-keyshortcuts="Control+Z Meta+Z" disabled={!actionHistory.undo || historyBusy || Boolean(activity.pending) || modalOpen || loading || bookLoading} title={actionHistory.blocked ? "Dữ liệu đã thay đổi từ phiên khác; hãy tải lại trang." : `Hoàn tác${actionHistory.undo ? `: ${actionHistory.undo}` : " (chưa có thao tác)"} · Ctrl+Z`} onMouseDown={(event) => event.preventDefault()} onClick={() => void runHistory("undo")}><Undo2 /><span>Hoàn tác</span><kbd>Ctrl Z</kbd></button>
              <button type="button" aria-keyshortcuts="Control+Y Meta+Y" disabled={!actionHistory.redo || historyBusy || Boolean(activity.pending) || modalOpen || loading || bookLoading} title={actionHistory.blocked ? "Dữ liệu đã thay đổi từ phiên khác; hãy tải lại trang." : `Làm lại${actionHistory.redo ? `: ${actionHistory.redo}` : " (chưa có thao tác)"} · Ctrl+Y`} onMouseDown={(event) => event.preventDefault()} onClick={() => void runHistory("redo")}><Redo2 /><span>Làm lại</span><kbd>Ctrl Y</kbd></button>
            </div>
            <button className="notification-button"><Bell /><span /></button>
            <div className="teacher-profile"><div>TL</div><span><b>Thầy Linh</b><small>Giáo viên Toán</small></span><ChevronDown /></div>
          </div>
        </header>

        <div className="dashboard-content" inert={historyBusy}>
          <div className="dashboard-heading">
            <div>
              <span className="dashboard-kicker">KHÔNG GIAN GIẢNG DẠY</span>
              <h1>{page === "diem" ? "Quản lý điểm học sinh" : pageLabels[page]}</h1>
              <p>{page === "diem" ? "Chỉnh sửa trực tiếp hoặc nhập dữ liệu từ Excel. Kéo tiêu đề cột để đổi thứ tự; nhấp đúp tên để đổi tên." : page === "tong-quan" ? "Theo dõi tình hình lớp học và mở nhanh các công việc thường dùng." : page === "hoc-sinh" ? "Quản lý trạng thái học sinh, chuyển lớp và giữ đầy đủ điểm số." : page === "bao-cao" ? "Theo dõi mức hoàn thành và kết quả theo từng cột điểm." : page === "cai-dat" ? "Quản lý lớp học và cấu trúc bảng điểm." : "Các bước sử dụng sổ điểm điện tử."}</p>
            </div>
            <div className="heading-actions">
              <label className="year-select"><span>Năm học</span><select value={selectedYear} onChange={(event) => setSelectedYear(event.target.value)}>{academicYears.map((year) => <option key={year.value} value={year.value}>{year.label}{year.isCurrent ? " · hiện tại" : ""}</option>)}</select></label>
              {(page === "diem" || page === "hoc-sinh") && <button className="dashboard-btn dashboard-btn--outline" onClick={() => setShowStudentImport(true)} disabled={!gradebook}><UserPlus /> Nhập học sinh</button>}
              {(page === "diem" || page === "bao-cao") && <button className="dashboard-btn dashboard-btn--outline" onClick={() => void exportGradebook()} disabled={!gradebook}><FileDown /> Xuất Excel</button>}
              {page === "diem" && <button className="dashboard-btn dashboard-btn--primary" onClick={() => gradebook?.columns[0] && openScoreImport(gradebook.columns[0].id)} disabled={!gradebook?.columns.length}><Upload /> Nhập điểm</button>}
            </div>
          </div>

          <section className="class-selector-card">
            <div className="class-selector-title">
              <span><GraduationCap /></span>
              <div>
                <b>Lớp đang giảng dạy</b>
                <small>{selectedClass ? `${selectedClass.name} · ${selectedClass.category === "extra" ? "Lớp dạy thêm" : "Lớp chính khóa"}` : "Chọn lớp để xem và cập nhật bảng điểm"}</small>
              </div>
            </div>
            <div className="class-category-pills" role="tablist" aria-label="Phân loại lớp">
              <button
                type="button"
                role="tab"
                aria-selected={classCategoryFilter === "all"}
                className={classCategoryFilter === "all" ? "active" : ""}
                onClick={() => handleCategoryFilterChange("all")}
              >
                Tất cả <span>{classes.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={classCategoryFilter === "regular"}
                className={classCategoryFilter === "regular" ? "active" : ""}
                onClick={() => handleCategoryFilterChange("regular")}
              >
                Chính khóa <span>{regularClasses.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={classCategoryFilter === "extra"}
                className={classCategoryFilter === "extra" ? "active" : ""}
                onClick={() => handleCategoryFilterChange("extra")}
              >
                Dạy thêm <span>{extraClasses.length}</span>
              </button>
            </div>
            <div className="class-tabs">
              {visibleClasses.map((classItem) => (
                <button
                  key={classItem.id}
                  className={selectedClassId === classItem.id ? "active" : ""}
                  style={{ "--class-color": classItem.color } as React.CSSProperties}
                  onClick={() => setSelectedClassId(classItem.id)}
                >
                  <div className="class-tab-header">
                    <span>{classItem.name}</span>
                    <span className={`class-type-badge class-type-badge--${classItem.category}`}>
                      {classItem.category === "extra" ? "Dạy thêm" : "Chính khóa"}
                    </span>
                  </div>
                  <small>{classItem.studentCount} học sinh</small>
                </button>
              ))}
              {!visibleClasses.length && (
                <div className="no-class-inline">
                  <b>{classCategoryFilter === "extra" ? "Chưa có lớp dạy thêm" : classCategoryFilter === "regular" ? "Chưa có lớp chính khóa" : `Chưa có lớp trong năm học ${selectedYear}`}</b>
                  <small>Dùng nút “Thêm lớp” để tạo lớp mới.</small>
                </div>
              )}
            </div>
            <div className="class-actions">
              {selectedClass && (
                <button
                  className="class-icon-button"
                  onClick={() => setClassModal("edit")}
                  title={`Chỉnh sửa hoặc xóa lớp ${selectedClass.name}`}
                >
                  <Pencil />
                </button>
              )}
              <button
                className="add-class-button"
                onClick={() => {
                  setClassModalDefaultCategory(classCategoryFilter === "extra" ? "extra" : "regular");
                  setClassModal("create");
                }}
              >
                <Plus />Thêm lớp
              </button>
            </div>
          </section>

          {(page === "diem" || page === "tong-quan") && <section className="metric-grid">
            <article><div className="metric-icon metric-icon--green"><Users /></div><div><span>ĐANG HỌC</span><b>{gradebook ? activeStudents.length : "—"}</b><small>{gradebook ? `${gradebook.students.length - activeStudents.length} học sinh nghỉ` : "học sinh"}</small></div></article>
            <article><div className="metric-icon metric-icon--sun"><TrendingUp /></div><div><span>ĐIỂM TRUNG BÌNH</span><b>{metrics.average?.toFixed(1) ?? "—"}</b><small>/ 10</small></div></article>
            <article><div className="metric-icon metric-icon--blue"><Cloud /></div><div><span>ĐÃ NHẬP ĐIỂM</span><b>{metrics.complete}%</b><small>toàn bộ cột</small></div></article>
            <article><div className="metric-icon metric-icon--coral"><AlertTriangle /></div><div><span>CẦN LƯU Ý</span><b>{metrics.below}</b><small>điểm dưới 5</small></div></article>
          </section>}

          {page === "tong-quan" && <div className="dashboard-page-grid">
            <section className="dashboard-panel"><h2>Tình hình lớp {selectedClass?.name || "học"}</h2><p>{gradebook ? `${gradebook.students.length} học sinh · ${gradebook.columns.length} cột điểm · ${metrics.complete}% điểm đã nhập · ${selectedClass?.category === "extra" ? "Lớp dạy thêm" : "Lớp chính khóa"}` : "Chọn hoặc thêm một lớp để bắt đầu."}</p><div className="dashboard-quick-actions"><button onClick={() => changePage("diem")}><BookOpenCheck /> Mở bảng điểm</button><button onClick={() => changePage("hoc-sinh")}><Users /> Danh sách học sinh</button><button onClick={() => changePage("bao-cao")}><BarChart3 /> Xem báo cáo</button></div></section>
            <section className="dashboard-panel">
              <h2>Các lớp trong năm học {selectedYear}</h2>
              <div className="overview-categories">
                <div className="overview-category-section">
                  <div className="overview-category-title">
                    <BookOpen size={16} />
                    <span>Lớp chính khóa ({regularClasses.length})</span>
                  </div>
                  <div className="overview-class-list">
                    {regularClasses.map((item) => (
                      <button key={item.id} onClick={() => { setSelectedClassId(item.id); changePage("diem"); }}>
                        <span className="overview-class-name">
                          <span className="class-dot" style={{ backgroundColor: item.color }} />
                          {item.name}
                        </span>
                        <small>{item.studentCount} học sinh</small>
                        <ChevronRight />
                      </button>
                    ))}
                    {!regularClasses.length && <p className="overview-empty-note">Chưa có lớp chính khóa nào.</p>}
                  </div>
                </div>

                <div className="overview-category-section">
                  <div className="overview-category-title">
                    <GraduationCap size={16} />
                    <span>Lớp dạy thêm ({extraClasses.length})</span>
                  </div>
                  <div className="overview-class-list">
                    {extraClasses.map((item) => (
                      <button key={item.id} onClick={() => { setSelectedClassId(item.id); changePage("diem"); }}>
                        <span className="overview-class-name">
                          <span className="class-dot" style={{ backgroundColor: item.color }} />
                          {item.name}
                        </span>
                        <small>{item.studentCount} học sinh</small>
                        <ChevronRight />
                      </button>
                    ))}
                    {!extraClasses.length && <p className="overview-empty-note">Chưa có lớp dạy thêm nào.</p>}
                  </div>
                </div>
              </div>
            </section>
          </div>}

          {page === "hoc-sinh" && <section className="dashboard-panel">
            <div className="dashboard-panel-header"><div><h2>Danh sách học sinh</h2><p>Đổi trạng thái hoặc chuyển lớp ngay tại từng học sinh. Học sinh nghỉ vẫn giữ hồ sơ và điểm.</p></div><label className="student-page-search"><Search /><input aria-label="Tìm học sinh" placeholder="Tìm SBD hoặc họ tên" value={search} onChange={(event) => setSearch(event.target.value)} /></label></div>
            <StudentStatusFilter value={statusFilter} onChange={setStatusFilter} total={gradebook?.students.length || 0} active={activeStudents.length} />
            <div className="dashboard-simple-table-wrap"><table className="dashboard-simple-table student-management-table"><thead><tr><th>Số báo danh</th><th>Họ và tên</th><th>Trạng thái</th><th>Cột đã có điểm</th><th>Trung bình</th><th>Thao tác</th></tr></thead><tbody>
              {studentPageRows.map((student) => <tr key={student.id} className={student.status === "inactive" ? "student-row--inactive" : ""}><td data-label="SBD">{student.studentCode}</td><td><b>{student.lastName} {student.firstName}</b></td><td><StudentStatusSelect student={student} onChange={changeStudentStatus} /></td><td data-label="Cột đã có điểm">{gradebook?.columns.filter((column) => student.scores[column.id] != null).length || 0} / {gradebook?.columns.length || 0}</td><td data-label="Trung bình">{gradebook ? averageOf(student, gradebook.columns)?.toFixed(1) || "—" : "—"}</td><td><button className="student-transfer-button" onClick={() => setTransferStudent(student)} aria-label={`Chuyển lớp cho ${student.lastName} ${student.firstName}`}><ArrowRightLeft /> Chuyển lớp</button></td></tr>)}
              {!studentPageRows.length && <tr><td colSpan={6}>{bookLoading ? "Đang tải học sinh…" : "Chưa có học sinh phù hợp. Thử đổi bộ lọc hoặc nhập danh sách học sinh."}</td></tr>}
            </tbody></table></div>
          </section>}


          {page === "bao-cao" && <section className="dashboard-panel"><h2>Tiến độ nhập điểm theo cột</h2><p>Thống kê học sinh đang học của lớp {selectedClass?.name || "đang chọn"} trong năm học {selectedYear}.</p><div className="progress-list">{gradebook?.columns.map((column) => { const values = activeStudents.map((student) => student.scores[column.id]).filter((value): value is number => typeof value === "number"); const percent = activeStudents.length ? Math.round(values.length / activeStudents.length * 100) : 0; const average = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null; return <article key={column.id}><div><b>{column.shortLabel}</b><span>{values.length}/{activeStudents.length} học sinh · trung bình {average?.toFixed(1) ?? "—"}</span><strong>{percent}%</strong></div><div className="progress-track"><i style={{ width: `${percent}%` }} /></div></article>; })}{!gradebook?.columns.length && <p>Chưa có cột điểm để lập báo cáo.</p>}</div></section>}

          {page === "cai-dat" && <div className="dashboard-page-grid"><section className="dashboard-panel"><h2>Quản lý lớp học</h2><p>Năm học {selectedYear}. Lớp đang chọn: <b>{selectedClass?.name || "Chưa chọn lớp"}</b> {selectedClass ? `(${selectedClass.category === "extra" ? "Lớp dạy thêm" : "Lớp chính khóa"})` : ""}. Chọn lớp phía trên để đổi tên, phân loại hoặc xóa, hoặc tạo lớp mới.</p><div className="dashboard-quick-actions"><button onClick={() => { setClassModalDefaultCategory(classCategoryFilter === "extra" ? "extra" : "regular"); setClassModal("create"); }}><Plus /> Thêm lớp</button><button disabled={!selectedClass} onClick={() => setClassModal("edit")}><Pencil /> Sửa lớp đang chọn</button></div></section><section className="dashboard-panel"><div className="dashboard-panel-header"><div><h2>Cột điểm của lớp {selectedClass?.name || "đang chọn"}</h2><p>Thêm, sửa hoặc xóa cột điểm. Kéo thả thứ tự tại trang Quản lý điểm.</p></div><button className="dashboard-btn dashboard-btn--outline" disabled={!gradebook} onClick={() => setShowColumnModal(true)}><Plus /> Thêm cột</button></div><div className="settings-column-list">{gradebook?.columns.map((column) => <div key={column.id}><span><b>{column.shortLabel}</b><small>Hệ số {column.weight} · {column.mode === "sum" ? "Trắc nghiệm + tự luận" : "Điểm trực tiếp"}</small></span><button onClick={() => setEditingColumn(column)} aria-label={`Sửa ${column.shortLabel}`}><Pencil /></button><button onClick={() => void deleteScoreColumn(column)} aria-label={`Xóa ${column.shortLabel}`}><Trash2 /></button></div>)}{!gradebook?.columns.length && <p>Chưa có cột điểm.</p>}</div></section></div>}

          {page === "huong-dan" && <section className="dashboard-panel guide-panel"><h2>Sử dụng sổ điểm</h2><ol><li><b>Chọn năm học và lớp.</b> Thêm lớp mới nếu chưa có, sau đó chọn lớp cần làm việc.</li><li><b>Nhập học sinh.</b> Dùng file Excel/CSV hoặc dán bảng có SBD và họ tên.</li><li><b>Nhập điểm.</b> Nhập trực tiếp từng ô, dán một cột điểm từ Excel vào ô bắt đầu, hoặc mở Nhập điểm để dán cột hay tải file có SBD và Điểm. Kiểm tra bản xem trước trước khi ghi; điểm đơn lẻ được lưu khi rời ô.</li><li><b>Sao chép cột điểm.</b> Bấm ô đầu, giữ Shift rồi bấm ô cuối trong cùng cột (hoặc dùng Shift + ↑/↓, Shift + Home/End). Nhấn Ctrl+C / Cmd+C rồi dán vào Excel hoặc Google Sheets. Vùng chọn giữ ô trống và thứ tự đang hiển thị; với cột tổng, chọn riêng trắc nghiệm, tự luận hoặc tổng. Nhấn Escape hoặc bấm nơi khác để bỏ chọn.</li><li><b>Quản lý cột.</b> Nhấp đúp tên để đổi tên; dùng các nút trên tiêu đề để nhập file, cấu hình hoặc xóa. Kéo biểu tượng trên tiêu đề sang cột khác để đổi thứ tự.</li><li><b>Trạng thái và chuyển lớp.</b> Tại Học sinh hoặc bảng điểm, chọn “Đang học / Nghỉ”. Dùng “Chuyển lớp”, chọn lớp mới trong cùng năm học, ghép cột rồi xem trước và xác nhận. Cột không khớp có thể tạo mới để giữ đủ điểm; SBD trùng cần đổi. Học sinh nghỉ giữ điểm nhưng không tính vào thống kê lớp.</li><li><b>Hoàn tác và làm lại.</b> Hai nút trên thanh đầu trang hoặc Ctrl+Z / Ctrl+Y khôi phục lớp, học sinh, cột và điểm đã lưu. Giữ tối đa 50 thao tác trong phiên đăng nhập, kể cả khi tải lại trang; thao tác mới xóa nhánh làm lại. Khi đang gõ, phím tắt xử lý nội dung ô trước; điểm đã lưu dùng lịch sử bảng điểm. Thay đổi từ phiên khác sẽ chặn lịch sử cũ để tránh ghi đè.</li><li><b>Xem kết quả.</b> Mở Học sinh hoặc Báo cáo tiến bộ; xuất bảng Excel từ trang Quản lý điểm.</li></ol></section>}

          {page === "diem" && <section className="gradebook-card">
            <div className="gradebook-display-options">
              <StudentStatusFilter value={statusFilter} onChange={setStatusFilter} total={gradebook?.students.length || 0} active={activeStudents.length} />
              <div className="gradebook-column-toggles" role="group" aria-label="Ẩn hiện cột bảng điểm">
                <span>Hiển thị cột:</span>
                <button aria-pressed={showStatusColumn} title={`${showStatusColumn ? "Ẩn" : "Hiện"} cột Trạng thái`} onClick={() => setShowStatusColumn((current) => !current)}>{showStatusColumn ? <Eye /> : <EyeOff />} Trạng thái</button>
                <button aria-pressed={showActionColumn} title={`${showActionColumn ? "Ẩn" : "Hiện"} cột Thao tác`} onClick={() => setShowActionColumn((current) => !current)}>{showActionColumn ? <Eye /> : <EyeOff />} Thao tác</button>
              </div>
            </div>
            <div className="gradebook-toolbar">
              <div className="search-field"><Search /><input placeholder="Tìm theo SBD hoặc tên học sinh…" value={search} onChange={(event) => setSearch(event.target.value)} /><kbd>⌘ K</kbd></div>
              {gradebook && <button className="add-score-column" onClick={() => setShowColumnModal(true)}><Plus size={16} /> Thêm cột điểm</button>}
              <div className="toolbar-meta"><Filter /> <span>Đang hiển thị <b>{filteredStudents.length}</b> / {gradebook?.students.length || 0} học sinh</span></div>
              {Object.values(filters).some(Boolean) && <button className="clear-filter" onClick={() => setFilters({})}>Xóa bộ lọc</button>}
            </div>

            <div className="gradebook-table-wrap">
              {bookLoading ? (
                <div className="table-loading"><LoaderCircle className="spin" /><p>Đang tải bảng điểm…</p></div>
              ) : !gradebook ? (
                <div className="empty-state empty-state--large"><GraduationCap /><b>Chưa có lớp học</b><span>Tạo lớp trong năm học {selectedYear}, sau đó nhập danh sách học sinh.</span><button className="dashboard-btn dashboard-btn--primary" onClick={() => setClassModal("create")}><Plus />Thêm lớp đầu tiên</button></div>
              ) : (
                <table ref={scoreTable} className="gradebook-table" onCopyCapture={copyScoreRange} onKeyDownCapture={onScoreRangeKeyDown}
                  onFocusCapture={(event) => { const cell = scoreTarget(event.target); if (cell) selectScore(cell, false); else setScoreSelection(null); }}
                  onChange={(event) => { const cell = scoreTarget(event.target); if (cell) selectScore(cell, false); }}
                  onPointerDownCapture={(event) => {
                    if (event.button !== 0) return;
                    const cell = scoreTarget(event.target);
                    if (!cell) return;
                    const extending = event.shiftKey && scoreRange && scoreRange.columnId === cell.dataset.scoreColumn && (scoreRange.component || "total") === cell.dataset.scoreComponent;
                    if (extending) event.preventDefault();
                    selectScore(cell, Boolean(extending));
                  }}
                  onPointerOverCapture={(event) => {
                    const cell = scoreTarget(event.target);
                    if (cell && event.buttons === 1 && event.shiftKey && scoreRange && scoreRange.columnId === cell.dataset.scoreColumn && (scoreRange.component || "total") === cell.dataset.scoreComponent) selectScore(cell, true);
                  }}>
                  <thead>
                    <tr className="table-title-row">
                      <th className="col-index">#</th>
                      <SortableHeader label="Số báo danh" columnKey="studentCode" sort={sort} onSort={toggleSort} className="col-code" />
                      <SortableHeader label="Họ và tên lót" columnKey="lastName" sort={sort} onSort={toggleSort} className="col-lastname" />
                      <SortableHeader label="Tên" columnKey="firstName" sort={sort} onSort={toggleSort} className="col-firstname" />
                      {showStatusColumn && <th className="col-status">Trạng thái</th>}
                      {gradebook.columns.map((column) => <ScoreColumnHeader key={column.id} column={column} sort={sort} onSort={toggleSort} onRename={renameScoreColumn} onImport={() => openScoreImport(column.id)} onConfigure={() => setEditingColumn(column)} onDelete={() => void deleteScoreColumn(column)} onError={(message) => showToast("error", message)} onDragStart={() => setDraggedColumnId(column.id)} onDragEnd={() => setDraggedColumnId(null)} onDrop={() => void reorderColumn(column.id)} dragging={draggedColumnId === column.id} />)}
                      {showActionColumn && <th className="col-student-action">Thao tác</th>}
                    </tr>
                    <tr className="filter-row">
                      <th />
                      <FilterInput placeholder="Lọc SBD" value={filters.studentCode || ""} onChange={(value) => setFilters({ ...filters, studentCode: value })} />
                      <FilterInput placeholder="Lọc họ" value={filters.lastName || ""} onChange={(value) => setFilters({ ...filters, lastName: value })} />
                      <FilterInput placeholder="Lọc tên" value={filters.firstName || ""} onChange={(value) => setFilters({ ...filters, firstName: value })} />
                      {showStatusColumn && <th />}
                      {gradebook.columns.map((column) => <FilterInput key={column.id} placeholder="vd. ≥8" value={filters[column.id] || ""} onChange={(value) => setFilters({ ...filters, [column.id]: value })} />)}
                      {showActionColumn && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student, index) => {
                      return (
                        <tr key={student.id} className={student.status === "inactive" ? "student-row--inactive" : ""}>
                          <td className="col-index">{String(index + 1).padStart(2, "0")}</td>
                          <td className="col-code"><b>{student.studentCode}</b></td>
                          <td className="col-lastname">{student.lastName}</td>
                          <td className="col-firstname"><b>{student.firstName}</b></td>
                          {showStatusColumn && <td className="col-status"><StudentStatusSelect student={student} onChange={changeStudentStatus} /></td>}
                          {gradebook.columns.map((column) => (
                            <td key={column.id}>
                              {column.mode === "sum" ? <div className="sum-score-cell">
                                <ScoreCell selected={isScoreSelected(student.id, column.id, "multipleChoice")} value={student.components[column.id]?.multipleChoice ?? null} studentId={student.id} columnId={column.id} component="multipleChoice" onSaved={(result) => updateLocalScore(student.id, column.id, result)} onError={(message) => showToast("error", message)} onPasteColumn={(text) => openScorePaste(column.id, student.studentCode, text, "multipleChoice")} />
                                <span>+</span>
                                <ScoreCell selected={isScoreSelected(student.id, column.id, "written")} value={student.components[column.id]?.written ?? null} studentId={student.id} columnId={column.id} component="written" onSaved={(result) => updateLocalScore(student.id, column.id, result)} onError={(message) => showToast("error", message)} onPasteColumn={(text) => openScorePaste(column.id, student.studentCode, text, "written")} />
                                <strong tabIndex={0} aria-label={`Tổng điểm ${column.shortLabel} của ${student.lastName} ${student.firstName}`} data-score-student={student.id} data-score-column={column.id} data-score-component="total" className={`score-total ${isScoreSelected(student.id, column.id) ? "is-selected" : ""}`}>{student.scores[column.id] ?? "—"}</strong>
                              </div> : <ScoreCell selected={isScoreSelected(student.id, column.id)} value={student.scores[column.id] ?? null} studentId={student.id} columnId={column.id} onSaved={(result) => updateLocalScore(student.id, column.id, result)} onError={(message) => showToast("error", message)} onPasteColumn={(text) => openScorePaste(column.id, student.studentCode, text)} />}
                            </td>
                          ))}
                          {showActionColumn && <td className="col-student-action"><button className="student-transfer-button" onClick={() => setTransferStudent(student)} aria-label={`Chuyển lớp cho ${student.lastName} ${student.firstName}`}><ArrowRightLeft /> Chuyển lớp</button></td>}
                        </tr>
                      );
                    })}
                    {filteredStudents.length === 0 && <tr><td colSpan={gradebook.columns.length + 4 + Number(showStatusColumn) + Number(showActionColumn)}><div className="empty-state">{gradebook.students.length ? <Search /> : <UserPlus />}<b>{gradebook.students.length ? "Không tìm thấy học sinh" : "Lớp chưa có học sinh"}</b><span>{gradebook.students.length ? "Thử thay đổi từ khóa hoặc điều kiện lọc." : "Nhập từ Excel hoặc dán trực tiếp từ clipboard."}</span>{!gradebook.students.length && <button className="dashboard-btn dashboard-btn--primary" onClick={() => setShowStudentImport(true)}><UserPlus />Nhập danh sách</button>}</div></td></tr>}
                  </tbody>
                </table>
              )}
            </div>
            <div className="score-selection-help" role="status">
              {selectedScoreStudents.length > 1 ? `Đã chọn ${selectedScoreStudents.length} ô điểm · Ctrl+C / Cmd+C để sao chép · Escape để bỏ chọn` : "Sao chép cột điểm: bấm ô đầu, giữ Shift và bấm ô cuối, rồi Ctrl+C / Cmd+C."}
            </div>
            <footer className="gradebook-footer">
              <div className="score-legend"><span><i className="legend-high" /> Tốt (≥ 8)</span><span><i className="legend-medium" /> Đạt (5–7.9)</span><span><i className="legend-low" /> Cần lưu ý (&lt; 5)</span></div>
              <span><Cloud /> Tự động lưu khi rời ô</span>
            </footer>
          </section>}
        </div>
      </main>

      {sidebarOpen && <div className="sidebar-scrim" onClick={() => setSidebarOpen(false)} />}
      {importColumnId && gradebook && <ImportModal gradebook={gradebook} orderedStudents={importStudentOrder} initialColumnId={importColumnId} initialComponent={pasteTarget?.component} initialClipboard={pasteTarget?.text} initialStartStudentCode={pasteTarget?.studentCode} onClose={closeScoreImport} onImported={(result) => void refreshAfterImport(result)} />}
      {showColumnModal && gradebook && <ScoreColumnModal onClose={() => setShowColumnModal(false)} onCreate={createScoreColumn} />}
      {editingColumn && <ScoreColumnModal key={editingColumn.id} column={editingColumn} hasScores={gradebook?.students.some((student) => student.scores[editingColumn.id] != null || student.components[editingColumn.id]?.multipleChoice != null || student.components[editingColumn.id]?.written != null)} onClose={() => setEditingColumn(null)} onCreate={configureScoreColumn} />}
      {showStudentImport && selectedClass && <StudentImportModal classItem={selectedClass} onClose={() => setShowStudentImport(false)} onImported={(result) => void refreshAfterStudentImport(result)} />}
      {transferStudent && gradebook && <StudentTransferModal student={transferStudent} gradebook={gradebook} classes={classes} onClose={() => setTransferStudent(null)} onTransferred={handleStudentTransferred} />}
      {classModal && <ClassModal academicYear={selectedYear} classItem={classModal === "edit" ? selectedClass : undefined} defaultCategory={classModalDefaultCategory} onClose={() => setClassModal(null)} onSaved={handleClassSaved} onDeleted={handleClassDeleted} />}
      {toast && <div className={`toast toast--${toast.type}`}>{toast.type === "success" ? <Check /> : <AlertTriangle />}<span>{toast.message}</span><button onClick={() => setToast(null)}><X /></button></div>}
      <button className="back-to-site" onClick={() => navigate("/")}><ArrowLeft /> Xem website</button>
    </div>
  );
}

type SortableHeaderProps = { label: string; columnKey: string; sort: SortState; onSort: (key: string) => void; className?: string };
function SortableHeader({ label, columnKey, sort, onSort, className = "" }: SortableHeaderProps) {
  const active = sort.key === columnKey;
  const descending = active && sort.direction === "desc";
  const SortIcon = columnKey === "studentCode" ? (descending ? ArrowUp01 : ArrowDown01) : (descending ? ArrowUpAZ : ArrowDownAZ);
  const action = `Sắp xếp ${label} ${active && sort.direction === "asc" ? "giảm" : "tăng"} dần`;
  return <th className={className} aria-sort={active ? (descending ? "descending" : "ascending") : "none"}><button className={active ? "active" : ""} aria-label={action} title={action} onClick={() => onSort(columnKey)}><span>{label}</span><span className="sort-indicator" aria-hidden="true"><SortIcon /></span></button></th>;
}

type ScoreColumnHeaderProps = {
  column: ScoreColumn;
  sort: SortState;
  onSort: (key: string) => void;
  onRename: (columnId: string, name: string) => Promise<void>;
  onImport: () => void;
  onConfigure: () => void;
  onDelete: () => void;
  onError: (message: string) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
  dragging: boolean;
};

function ScoreColumnHeader({ column, sort, onSort, onRename, onImport, onConfigure, onDelete, onError, onDragStart, onDragEnd, onDrop, dragging }: ScoreColumnHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(column.shortLabel);
  const [saving, setSaving] = useState(false);
  const cancelled = useRef(false);
  const active = sort.key === column.id;
  const sortAction = `Sắp xếp ${column.shortLabel} ${active && sort.direction === "asc" ? "giảm" : "tăng"} dần`;

  const startEditing = () => {
    setDraft(column.shortLabel);
    cancelled.current = false;
    setEditing(true);
  };

  const finishEditing = async () => {
    if (cancelled.current) {
      cancelled.current = false;
      setEditing(false);
      return;
    }
    const name = draft.trim().replace(/\s+/g, " ");
    if (name === column.shortLabel) {
      setEditing(false);
      return;
    }
    if (!name || name.length > 40) {
      onError("Tên cột điểm phải có từ 1 đến 40 ký tự.");
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onRename(column.id, name);
      setEditing(false);
    } catch (error) {
      onError(error instanceof Error ? error.message : "Không thể đổi tên cột điểm.");
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <th className={`score-column-heading ${dragging ? "is-dragging" : ""}`} aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); onDrop(); }}>
      {editing ? (
        <input
          autoFocus
          aria-label={`Đổi tên cột ${column.shortLabel}`}
          maxLength={40}
          value={draft}
          disabled={saving}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => void finishEditing()}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            if (event.key === "Escape") {
              cancelled.current = true;
              event.currentTarget.blur();
            }
          }}
        />
      ) : (
        <div className="score-column-heading__content">
          <button className="score-column-heading__drag" draggable aria-label={`Kéo để đổi vị trí cột ${column.shortLabel}`} title="Kéo để đổi vị trí cột" onDragStart={(event) => { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", column.id); onDragStart(); }} onDragEnd={onDragEnd}><GripVertical /></button>
          <button className="score-column-heading__name" title="Nhấp đúp để đổi tên cột điểm" onDoubleClick={startEditing} onKeyDown={(event) => { if (event.key === "Enter") startEditing(); }}>
            <span>{column.shortLabel}</span>
          </button>
          <button className={`score-column-heading__sort ${active ? "active" : ""}`} aria-label={sortAction} title={sortAction} onClick={() => onSort(column.id)}>
            <span className="sort-indicator" aria-hidden="true">{active && sort.direction === "desc" ? <ArrowUp01 /> : <ArrowDown01 />}</span>
          </button>
          <div className="score-column-heading__actions">
            <button onClick={onImport} title={`Nhập file cho ${column.shortLabel}`} aria-label={`Nhập file cho ${column.shortLabel}`}><Upload /></button>
            <button onClick={onConfigure} title={`Cấu hình cột ${column.shortLabel}`} aria-label={`Cấu hình cột ${column.shortLabel}`}><Settings2 /></button>
            <button onClick={onDelete} title={`Xóa cột ${column.shortLabel}`} aria-label={`Xóa cột ${column.shortLabel}`}><Trash2 /></button>
          </div>
        </div>
      )}
    </th>
  );
}

type FilterInputProps = { placeholder: string; value: string; onChange: (value: string) => void };
function FilterInput({ placeholder, value, onChange }: FilterInputProps) {
  return <th><div className="column-filter"><Filter /><input aria-label={placeholder} placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} /></div></th>;
}

function StudentStatusFilter({ value, onChange, total, active }: { value: Student["status"] | "all"; onChange: (value: Student["status"] | "all") => void; total: number; active: number }) {
  return <div className="student-status-filters" role="group" aria-label="Lọc trạng thái học sinh">
    <button aria-pressed={value === "all"} onClick={() => onChange("all")}>Tất cả <span>{total}</span></button>
    <button aria-pressed={value === "active"} onClick={() => onChange("active")}>Đang học <span>{active}</span></button>
    <button aria-pressed={value === "inactive"} onClick={() => onChange("inactive")}>Nghỉ <span>{total - active}</span></button>
  </div>;
}
